import { api_fetch, Cell, QueryData } from "@/api"
import { LoadingSpinner } from "@/app/loading"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.tsx"
import { cn, currencyFormatter, map, toISODateString, useAsyncEffect } from "@/utils"
import { useState } from "preact/compat"
import { GlobalFilterState } from "@/global_filter"

export function SummaryCards() {
  const [data, setData] = useState<QueryData>()
  const [loading, setLoading] = useState(true)
  // const { transactionTypes } = TableState.use()
  const [start, end, distributor, dateKind] = GlobalFilterState.use(s => [
    s.startDate,
    s.endDate,
    s.distributor,
    s.dateKind,
  ])

  useAsyncEffect(async () => {
    let res = await api_fetch<QueryData>(`/deductions/query`, {
      params: {
        start_date: map(start, toISODateString),
        end_date: map(end, toISODateString),
        distributor,
        date_kind: dateKind,
        query_name: "summary",
      },
    })
    setLoading(false)
    // A request the browser dropped (navigating away, a flaky connection) leaves no cards, not a crash.
    setData(res.ok ? res.value.data : undefined)
  }, [start, end, distributor, dateKind])

  if (loading) {
    return <LoadingSpinner />
  }

  const row = data?.rows[0]
  if (!row) {
    return null
  }
  const salesTotal = Math.abs(row[0] as number)
  const deductionsTotal = Math.abs(row[2] as number)
  const tradeRate = (deductionsTotal / salesTotal) * 100
  const wonTotal = Math.abs(row[6] as number)
  const winRate = (wonTotal / deductionsTotal) * 100
  const shouldShowWinRate = winRate >= 3

  return (
    <div className="flex flex-row space-x-4 mb-6 mt-8">
      <Card className="h-fit flex-1">
        <CardBody
          value={row[0]}
          total={`${row[1]} purchase orders`}
          title="Total Sales"
          valueFormatter={currencyFormatter}
        />
      </Card>

      <Card className="h-fit flex-1">
        <CardBody
          value={row[2]}
          total={`${row[3]} deductions on invoices`}
          title="Total Deductions"
          valueFormatter={currencyFormatter}
          color="text-red-800"
          rightValue={tradeRate}
          rightColor="text-red-800"
          rightValueFormatter={(v: number) => `${Math.round(v)}%`}
          rightTitle="Deduction Rate"
        />
      </Card>

      {row[6] !== 0 && (
        <Card className="h-fit flex-1">
          <CardBody
            value={row[6]}
            total={`${row[7]} repayments`}
            title="Repayments"
            valueFormatter={currencyFormatter}
            color="text-green-600"
            rightValue={shouldShowWinRate ? winRate : undefined}
            rightColor="text-green-600"
            rightValueFormatter={(v: number) => `${Math.round(v)}%`}
            rightTitle={shouldShowWinRate ? "% of Deductions Repaid" : undefined}
          />
        </Card>
      )}
    </div>
  )
}

interface SummaryCardData {
  title?: string
  color?: string
  total?: Cell
  value: Cell
  valueFormatter?: (value: number) => string
  onClick?: () => void
  rightValue?: Cell
  rightColor?: string
  rightValueFormatter?: (value: number) => string
  rightTitle?: string
  rightTotal?: Cell
}

export function CardBody({
  title,
  color,
  total,
  value,
  valueFormatter,
  onClick,
  rightValue,
  rightColor,
  rightValueFormatter,
  rightTitle,
  rightTotal,
}: SummaryCardData) {
  return (
    <div onClick={onClick}>
      {(title || rightTitle) && (
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-md font-medium flex items-center">{title}</CardTitle>
          {rightTitle && <CardTitle className="text-md font-medium">{rightTitle}</CardTitle>}
        </CardHeader>
      )}
      <CardContent>
        <div className="flex justify-between items-start">
          <div>
            <div className={cn("text-3xl font-bold", color)}>
              {valueFormatter ? valueFormatter(value as number) : value}
            </div>
            {total && <p className="text-sm text-muted-foreground pt-4">{total}</p>}
          </div>
          {rightValue && (
            <div>
              <div className={cn("text-3xl font-bold text-right min-w-[120px]", rightColor)}>
                {rightValueFormatter ? rightValueFormatter(rightValue as number) : rightValue}
              </div>
              {rightTotal && <p className="text-sm text-muted-foreground pt-4 text-right">{rightTotal}</p>}
            </div>
          )}
        </div>
      </CardContent>
    </div>
  )
}
